// src/components/shared/DashboardTradeShareModal.jsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineDownload, HiX, HiOutlineClipboardCopy } from 'react-icons/hi';
import TradeShareCard from '../strategies/TradeShareCard';

const normalizeTime = (ts) => {
  if (!ts) return null;
  if (typeof ts === 'string' && ts.length > 13) return parseInt(ts.slice(0, 13), 10);
  if (typeof ts === 'number') return ts;
  const parsed = new Date(ts).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const getLocale = (lang) => (lang && lang.toLowerCase().startsWith('ru') ? 'ru-RU' : 'en-US');

const formatDate = (timestamp, lang) => {
  const ms = normalizeTime(timestamp);
  if (!ms) return '-';
  return new Date(ms).toLocaleDateString(getLocale(lang), { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatTime = (timestamp, lang) => {
  const ms = normalizeTime(timestamp);
  if (!ms) return '-';
  return new Date(ms).toLocaleTimeString(getLocale(lang), { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const normalizeSide = (side) => {
  if (!side) return '';
  const upper = String(side).toUpperCase();
  if (upper === 'BUY' || upper === 'LONG') return 'LONG';
  if (upper === 'SELL' || upper === 'SHORT') return 'SHORT';
  return '';
};

const pickLeg = (legs, symbol) => {
  if (!Array.isArray(legs)) return null;
  return legs.find((leg) => leg.symbol === symbol) || legs[0] || null;
};

const buildShareData = (trade, lang) => {
  const exData = trade.exchangeData || {};
  const entries = exData.entries || [];
  const realized = exData.realized || {};

  const pairA = trade.symbolA || '?';
  const pairB = trade.symbolB || '?';

  const mappedEntries = entries.map((entry, index) => {
    const legA = pickLeg(entry.legs, pairA);
    const legB = pickLeg(entry.legs, pairB);
    return {
      id: index + 1,
      time: formatTime(entry.entryTime, lang),
      zScore: entry.z,
      level: entry.level,
      pos_a: normalizeSide(legA?.side),
      pos_b: normalizeSide(legB?.side),
      price_a: legA?.priceRef,
      price_b: legB?.priceRef,
      quantity_a: legA?.qty,
      quantity_b: legB?.qty
    };
  });

  const exitLegA = pickLeg(realized.legs, pairA);
  const exitLegB = pickLeg(realized.legs, pairB);

  const dateSource = realized.windowEnd || realized.fetchedAtMs || exData.lastActionAt || trade.closedAtMs || trade.updatedAt;

  return {
    pairA,
    pairB,
    date: formatDate(dateSource, lang),
    strategyName: trade.customStrategyName || trade.strategyName,
    commission: realized.totalFees,
    entries: mappedEntries.length
      ? mappedEntries
      : [{
          id: 1,
          time: formatTime(trade.closedAtMs || trade.updatedAt, lang),
          zScore: null,
          level: null,
          pos_a: normalizeSide(exitLegA?.side),
          pos_b: normalizeSide(exitLegB?.side),
          price_a: exitLegA?.avgEntryPrice,
          price_b: exitLegB?.avgEntryPrice,
          quantity_a: exitLegA?.qty,
          quantity_b: exitLegB?.qty
        }],
    exit: {
      priceA: exitLegA?.avgExitPrice,
      priceB: exitLegB?.avgExitPrice,
      pnlA: exitLegA?.closedPnl,
      pnlB: exitLegB?.closedPnl,
      totalPnl: realized.totalClosedPnl
    }
  };
};

const dataUrlToBlob = (dataUrl) => {
  const [meta, base64] = dataUrl.split(',');
  const mime = meta.match(/:(.*?);/)?.[1] || 'image/png';
  const bytes = atob(base64);
  const array = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) array[i] = bytes.charCodeAt(i);
  return new Blob([array], { type: mime });
};

const DashboardTradeShareModal = ({ trade, onClose }) => {
  const { t, i18n } = useTranslation();
  const [imageUrl, setImageUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const cardRef = useRef(null);

  const shareData = useMemo(() => {
    if (!trade) return null;
    return buildShareData(trade, i18n.language);
  }, [trade, i18n.language]);

  useEffect(() => {
    if (!trade) return;
    let cancelled = false;

    const generate = async () => {
      if (!cardRef.current) return;
      setIsGenerating(true);
      setError('');
      setImageUrl('');

      try {
        const { default: html2canvas } = await import('html2canvas');
        await new Promise((resolve) => setTimeout(resolve, 60));
        const canvas = await html2canvas(cardRef.current, {
          scale: 2.6,
          backgroundColor: '#0B1120',
          logging: false,
          useCORS: true,
          allowTaint: true
        });
        if (!cancelled) {
          setImageUrl(canvas.toDataURL('image/png'));
        }
      } catch (err) {
        if (!cancelled) setError(t('dashboard.share_modal.error_generate'));
      } finally {
        if (!cancelled) setIsGenerating(false);
      }
    };

    generate();
    return () => { cancelled = true; };
  }, [trade, shareData, t]);

  if (!trade) return null;

  const handleDownload = () => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `hedger_trade_${shareData?.pairA}_${shareData?.pairB}.png`;
    link.click();
  };

  const handleCopy = async () => {
    if (!imageUrl) return;
    setError('');
    try {
      const blob = dataUrlToBlob(imageUrl);
      if (navigator?.clipboard?.write && typeof ClipboardItem !== 'undefined') {
        const item = new ClipboardItem({ [blob.type]: blob });
        await navigator.clipboard.write([item]);
        return;
      }
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(imageUrl);
        return;
      }
      throw new Error('Clipboard API not available');
    } catch (err) {
      setError(t('dashboard.share_modal.error_copy'));
    }
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-3xl m-4 rounded-2xl border border-slate-700 bg-slate-900/95 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div>
            <h3 className="text-lg font-semibold text-white">{t('dashboard.share_modal.title')}</h3>
            <p className="text-xs text-slate-400">{t('dashboard.share_modal.subtitle')}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full text-slate-400 hover:bg-slate-800 hover:text-white">
            <HiX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-5 max-h-[80vh] overflow-auto">
          {isGenerating && (
            <div className="flex items-center justify-center h-[420px] rounded-xl border border-slate-800 bg-slate-900/50 text-slate-400 animate-pulse">
              {t('dashboard.share_modal.generating')}
            </div>
          )}

          {!isGenerating && imageUrl && (
            <div className="flex justify-center">
              <img
                src={imageUrl}
                alt="Trade share preview"
                className="max-h-[620px] w-auto rounded-xl border border-slate-800 shadow-xl"
              />
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
              {error}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <button
              onClick={handleDownload}
              disabled={!imageUrl || isGenerating}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800 disabled:opacity-50"
            >
              <HiOutlineDownload className="w-4 h-4" />
              {t('dashboard.share_modal.download')}
            </button>
            <button
              onClick={handleCopy}
              disabled={!imageUrl || isGenerating}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 hover:bg-blue-500 disabled:opacity-50"
            >
              <HiOutlineClipboardCopy className="w-4 h-4" />
              {t('dashboard.share_modal.copy')}
            </button>
          </div>
        </div>

        <div className="fixed -left-[10000px] top-0 pointer-events-none">
          <div ref={cardRef}>
            <TradeShareCard data={shareData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTradeShareModal;
